import { computed } from "vue";

/**
 * Table configuration for OperatingCurrenciesTable.
 *
 * Columns (Figma node 943:3379): Currency · Symbol · Exchange rate · Status ·
 * Rate mode · Chatter · Menu. The Reference badge is rendered inline inside the
 * Currency cell (CurrencyWithFlagsCard), not as a standalone column.
 *
 * Row actions are surfaced as callbacks so the parent component can emit them
 * upward — the shared table stays free of API calls (PR checklist §4/§5).
 *
 * @param {object} handlers
 * @param {(payload: {item: object, mode: string}) => void} [handlers.onRateModeChange]
 * @param {(payload: {item: object, rate: number}) => void} [handlers.onSetRate]
 * @param {(payload: {item: object, action: string}) => void} [handlers.onMenuAction]
 * @param {(payload: {item: object}) => void} [handlers.onChatterClick]
 */
export function useTableConfig(handlers = {}) {
  const {
    onRateModeChange = () => {},
    onSetRate = () => {},
    onMenuAction = () => {},
    onChatterClick = () => {},
  } = handlers;

  const isReference = (item) => item.is_reference === true;
  const isRetired = (item) =>
    item.active === false || item.status === "retired";
  const rateMode = (item) =>
    item.rate_mode === "corporate" ? "corporate" : "daily";

  // Rates are Decimal(18,6) — render six decimal places.
  const formatRate = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(6) : "";
  };

  const statusValue = (item) =>
    item.status || (item.active === false ? "retired" : "active");

  const tableParams = computed(() => {
    return {
      headers: [
        { text: "Currency", sortable: false, value: "name", width: "34%" },
        { text: "Symbol", sortable: false, value: "symbol", width: "8%" },
        {
          text: "Exchange rate",
          sortable: false,
          value: "exchange_rate_to_reference",
          width: "20%",
        },
        { text: "Status", sortable: false, value: "status", width: "12%" },
        { text: "Rate mode", sortable: false, value: "rate_mode", width: "16%" },
        { text: "", sortable: false, value: "chatter", width: "5%" },
        { text: "", sortable: false, value: "actions", width: "5%" },
      ],
      columns: [
        {
          field: "name",
          component: "CurrencyWithFlagsCard",
          props: (item) => ({
            currency_name: item.name || "",
            currency_code: item.code || "",
            is_reference: isReference(item),
            retired: isRetired(item),
          }),
          events: () => ({}),
        },
        {
          field: "symbol",
          value: (item) => item.symbol || "",
        },
        {
          field: "exchange_rate_to_reference",
          component: "OperatingCurrencyRateCell",
          // Reference row shows a locked 1.000000; Auto rows are read-only with an
          // "Updated <date>" sub-label; Corporate rows are editable with a
          // "Set manually <date>" sub-label (Rules 1 & 8, AC).
          props: (item) => ({
            value: formatRate(item.exchange_rate_to_reference),
            rate_mode: rateMode(item),
            is_reference: isReference(item),
            retired: isRetired(item),
            rate_updated_at: item.rate_updated_at || "",
            loading: item.rate_loading === true,
            error: item.rate_error || "",
          }),
          events: (item) => ({
            commit: (rate) => onSetRate({ item, rate }),
          }),
        },
        {
          field: "status",
          component: "Badge",
          props: (item) => ({
            status: statusValue(item),
          }),
        },
        {
          field: "rate_mode",
          component: "OperatingCurrencyRateModeCell",
          props: (item) => ({
            rate_mode: rateMode(item),
            is_reference: isReference(item),
            retired: isRetired(item),
            loading: item.rate_mode_loading === true,
          }),
          events: (item) => ({
            change: ({ mode }) => onRateModeChange({ item, mode }),
          }),
        },
        {
          field: "chatter",
          component: "ChatterIcon",
          // Simple gray icon on every row — matches OperatingCountryTable / CurrencyTable.
          props: () => ({
            unread: 0,
          }),
          events: (item) => ({
            onClick: () => onChatterClick({ item }),
          }),
        },
        {
          field: "actions",
          component: "OperatingCurrenciesLineMenu",
          props: (item) => ({
            active: item.active !== false && item.status !== "retired",
            is_reference: isReference(item),
            rate_mode: rateMode(item),
          }),
          events: (item) => ({
            action: (payload) =>
              onMenuAction({ item, action: payload?.type }),
          }),
        },
      ],
      filters: {
        values: [],
        searchText: "",
      },
      hideTopSection: true,
      hideActiveNo: true,
      activeRecords: {
        field: "status",
        value: "active",
      },
      defaultOrderingField: "name",
      activeRecordsCount: 0,
      hideGridView: true,
      // Fixed "Filter" label per the component set (Figma node 1130:52) —
      // overrides UniversalTable's Filters/More Filters default.
      filterBtnLabel: "Filter",
      // Hug icon + "Filter" text (Figma); opt-in so other tables keep 134px.
      filterBtnFitContent: true,
      actionLabel: "Add Operating Currency",
      title: "",
      orderingStorageKey: "currencies.ordering",
      cellContainerMaxWidth: "260px",
      itemsPerPage: 10,
    };
  });

  return {
    tableParams,
  };
}
